import ChatCard from "components/DesignSystem/ChatCard/v2/ChatCard";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { formatDate } from "utils/formatDate";
import { useLazyGetAllTransactionsDataQuery } from "store/apis/transactions";
import { useAppSelector } from "hooks/useAppSelector";
import qs from "qs";
import { COMMENT_TYPE, CommentType } from "store/apis/notes";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useLazyGetAllJournalEntriesQuery } from "store/apis/journalEntry";
import { ChatIcon } from "components/icons/LeftNav/ChatIcon";
import { openLink } from "utils/openLink";
import { useHistory, useLocation } from "react-router-dom";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";

const TRANSACTIONS_PATH = "/books/transactions";

type CtaData = {
  metadata: {
    object_id?: string;
    note_id?: string;
    entity_id: string;
    comment_type: CommentType;
    report_id?: string;
    group_id?: string;
  };
  url_text: string;
  document_operation: string;
};

type CommentChatCardProps = {
  customData: {
    description: string;
    header: string;
    cta_data: CtaData[];
    metadata: {
      comment_type: CommentType;
      object_id?: string;
      note_id?: string;
      entity_id: string;
      created_at?: string;
    };
    card_status: string;
  };
  groupId: string;
};

export const COMMENT_TYPE_LABEL: Record<CommentType, string> = {
  [COMMENT_TYPE.TRANSACTION_COMMENT]: "Transactions",
  [COMMENT_TYPE.LEDGER_ENTRY_COMMENT]: "Journal Entry",
  [COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT]: "Chart of Accounts",
  [COMMENT_TYPE.GENERAL_LEDGER_COMMENT]: "General Ledger",
  [COMMENT_TYPE.FINANCIAL_CLOSING_COMMENT]: "Financial Closing",
  [COMMENT_TYPE.BALANCE_SHEET_COMMENT]: "Balance Sheet",
  [COMMENT_TYPE.INCOME_STATEMENT_COMMENT]: "Income Statement",
  [COMMENT_TYPE.CASH_FLOW_COMMENT]: "Cash Flow",
  [COMMENT_TYPE.VENDOR_SUMMARY_COMMENT]: "Vendor Summary",
  [COMMENT_TYPE.CUSTOM_REPORT_COMMENT]: "Custom Report",
  [COMMENT_TYPE.TICKET_COMMENT]: "Tickets",
};

const getRedirectLink = ({
  ctaItem,
  groupId,
}: {
  ctaItem: CtaData;
  groupId: string;
}): string | null => {
  const { comment_type, object_id, entity_id } = ctaItem.metadata;

  const categoryParams = {
    company: groupId,
    entity: entity_id,
    selected_category_id: object_id,
    open_comment: object_id,
  };

  if (comment_type === COMMENT_TYPE.TRANSACTION_CATEGORY_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/chart-of-accounts${query}`;
  }

  if (comment_type === COMMENT_TYPE.GENERAL_LEDGER_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/categories${query}`;
  }

  if (comment_type === COMMENT_TYPE.FINANCIAL_CLOSING_COMMENT) {
    const query = qs.stringify(
      { company: groupId, entity: entity_id },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/financial-closing/${object_id}${query}`;
  }

  if (comment_type === COMMENT_TYPE.BALANCE_SHEET_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/live-financials/balance-sheet${query}`;
  }

  if (comment_type === COMMENT_TYPE.INCOME_STATEMENT_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/live-financials/income-statement${query}`;
  }

  if (comment_type === COMMENT_TYPE.CASH_FLOW_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/live-financials/cash-flow-statement${query}`;
  }

  if (comment_type === COMMENT_TYPE.VENDOR_SUMMARY_COMMENT) {
    const query = qs.stringify(categoryParams, {
      skipNulls: true,
      addQueryPrefix: true,
    });
    return `/books/live-financials/vendor-summary${query}`;
  }

  if (comment_type === COMMENT_TYPE.CUSTOM_REPORT_COMMENT) {
    const { group_id, report_id } = ctaItem.metadata;
    const compositeId =
      group_id && object_id ? `${group_id}-${object_id}` : object_id;
    const query = qs.stringify(
      {
        company: groupId,
        entity: entity_id,
        selected_category_id: object_id,
        open_comment: compositeId,
      },
      { skipNulls: true, addQueryPrefix: true }
    );
    return `/books/live-financials/custom-report/${report_id}${query}`;
  }

  return null;
};

const TransactionViewButton = ({
  ctaItem,
  groupId,
}: {
  ctaItem: CtaData;
  groupId: string;
}) => {
  const { alertToast } = useToast();
  const allFilters = useAppSelector(
    (state) => state.transactionCardPreview.filtersApplied
  );
  const [getTransactionPageNum, { isLoading }] =
    useLazyGetAllTransactionsDataQuery();
  const history = useHistory();
  const { pathname } = useLocation();

  const { object_id, entity_id } = ctaItem.metadata;

  const handleView = async () => {
    try {
      const data = await getTransactionPageNum({
        ...allFilters,
        jump_txn_id: object_id,
        fetch_page_num: true,
        entityId: entity_id,
        groupId,
      }).unwrap();

      if (!data?.page_number) {
        alertToast({
          message: "Transaction not found",
        });
        return;
      }

      const query = qs.stringify(
        {
          company: groupId,
          entity: entity_id,
          page: data?.page_number || 1,
          selected_transaction_id: object_id,
          open_comment: object_id,
        },
        { skipNulls: true, addQueryPrefix: true }
      );

      const isOnTransactionsPage = pathname.includes(TRANSACTIONS_PATH);
      if (isOnTransactionsPage) {
        history.push(`${TRANSACTIONS_PATH}${query}`);
        return;
      }
      openLink(`${TRANSACTIONS_PATH}${query}`, "_blank");
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Button
      size="small"
      customType="link"
      isLoading={isLoading}
      onClick={handleView}
    >
      Reply
    </Button>
  );
};

const LedgerEntryViewButton = ({
  ctaItem,
  groupId,
}: {
  ctaItem: CtaData;
  groupId: string;
}) => {
  const { alertToast } = useToast();
  const [getJournalEntryPageNum, { isLoading }] =
    useLazyGetAllJournalEntriesQuery();

  const { object_id, entity_id } = ctaItem.metadata;

  const handleView = async () => {
    try {
      const data = await getJournalEntryPageNum({
        groupId,
        entityId: entity_id,
        fetch_page_num: true,
        jump_ledger_entry_id: object_id,
      }).unwrap();

      const query = qs.stringify(
        {
          company: groupId,
          entity: entity_id,
          page: data?.page_number || 1,
          active_journal_entry: object_id,
          open_comment: object_id,
        },
        { skipNulls: true, addQueryPrefix: true }
      );
      openLink(`/books/journal-entries${query}`, "_blank");
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Button
      size="small"
      customType="link"
      isLoading={isLoading}
      onClick={handleView}
    >
      Reply
    </Button>
  );
};

export const CommentChatCard = ({
  customData,
  groupId: channelGroupId,
}: CommentChatCardProps) => {
  const { uuid: currentGroupId } = useCurrentChatGroupContext();
  const { description, cta_data, metadata } = customData;
  const { created_at } = metadata;
  const groupId = channelGroupId ?? currentGroupId;

  const commentType = metadata.comment_type;
  const commentLabel = COMMENT_TYPE_LABEL[commentType] || commentType;
  const ctaItem = cta_data?.[0];

  const isTransactionComment = commentType === COMMENT_TYPE.TRANSACTION_COMMENT;
  const isLedgerEntryComment =
    commentType === COMMENT_TYPE.LEDGER_ENTRY_COMMENT;

  const viewUrl =
    !isTransactionComment && !isLedgerEntryComment
      ? getRedirectLink({ ctaItem, groupId })
      : null;

  return (
    <div className="t-min-w-64">
      <ChatCard.Content addMargin={false}>
        <ChatCard.Header
          icon={<ChatIcon className="t-size-6 t-text-neutral" />}
        >
          <div className="t-text-subtext-sm t-text-text-30">Comments</div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-flex t-flex-col t-gap-1 t-bg-surface">
            <div className="t-flex t-items-center t-justify-between t-gap-2">
              <p className="t-m-0 t-text-subtext-sm t-text-text-60 t-line-clamp-2">
                {description}
              </p>
              {isTransactionComment && (
                <TransactionViewButton ctaItem={ctaItem} groupId={groupId} />
              )}
              {isLedgerEntryComment && (
                <LedgerEntryViewButton ctaItem={ctaItem} groupId={groupId} />
              )}
              {viewUrl && (
                <Link to={viewUrl} target="_blank">
                  <Button size="small" customType="link">
                    Reply
                  </Button>
                </Link>
              )}
            </div>
            {created_at && (
              <p className="t-m-0 t-text-body-sm t-text-text-30">
                {formatDate(created_at)}
              </p>
            )}
            <p className="t-m-0 t-text-caption-italic t-text-text-30 t-italic">
              {commentLabel}
            </p>
          </div>
        </ChatCard.Body>
      </ChatCard.Content>
    </div>
  );
};
